import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from "@angular/core";
import { MessageModule } from "primeng/message";

import { Area, SyncableStatus } from "../../../../../../../core/database/db";
import { BottomSheetComponent } from "../../../../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { CONSTANTS } from "../../../../../../../constants";
import { OpenItemsDataService } from "../../../services/open-items-data.service";

type ZoneGroup = "interior" | "exterior";

type SelectableArea = Area & {
  zoneGroup: ZoneGroup;
};

@Component({
  selector: "app-assign-room-bottom-sheet",
  standalone: true,
  imports: [
    CommonModule,
    BottomSheetComponent,
    MessageModule
  ],
  templateUrl: "./assign-room-bottom-sheet.component.html",
  styleUrls: ["./assign-room-bottom-sheet.component.scss"]
})
export class AssignRoomBottomSheetComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() selectedIssueCount = 0;

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() roomAssigned = new EventEmitter<Area>();

  private readonly defaultZoneGroup: ZoneGroup = "interior";

  openItemsDataService = inject(OpenItemsDataService);
  loading = signal(false);
  roomOptions = signal<SelectableArea[]>([]);
  selectedZoneGroup = signal<ZoneGroup>(this.defaultZoneGroup);
  selectedAreaIdentifier = signal<string | null>(null);

  areas = computed(() => this.openItemsDataService.areas() ?? (() => {
    throw new Error("Area data is not available in OpenItemsDataService.");
  })());

  zones = computed(() => this.openItemsDataService.zones() ?? (() => {
    throw new Error("Zone data is not available in OpenItemsDataService.");
  })());

  hasInteriorRooms = computed(() =>
    this.roomOptions().some(area => area.zoneGroup === CONSTANTS.ZONES.INTERIOR)
  );

  hasExteriorRooms = computed(() =>
    this.roomOptions().some(area => area.zoneGroup === CONSTANTS.ZONES.EXTERIOR)
  );

  availableRooms = computed(() =>
    this.roomOptions().filter(area => area.zoneGroup === this.selectedZoneGroup())
  );

  showNoRoomsMessage = computed(() =>
    !this.loading() && this.availableRooms().length === 0
  );

  ngOnChanges(changes: SimpleChanges): void {
    const shouldLoadRooms =
      (changes["isOpen"]?.currentValue === true && changes["isOpen"]?.previousValue !== true) ||
      (changes["lotId"] && this.isOpen);

    if (shouldLoadRooms) {
      void this.loadRooms();
    }

    if (changes["isOpen"]?.currentValue === false) {
      this.selectedAreaIdentifier.set(null);
    }
  }

  async loadRooms(): Promise<void> {
    this.loading.set(true);

    try {
      const zones = this.zones();
      const zoneGroupById = new Map<number, ZoneGroup>();

      zones.forEach(zone => {
        const normalizedName = zone.name?.trim().toLowerCase() ?? "";
        const zoneGroup = normalizedName.includes(CONSTANTS.ZONES.EXTERIOR)
          ? CONSTANTS.ZONES.EXTERIOR as ZoneGroup
          : CONSTANTS.ZONES.INTERIOR as ZoneGroup;
        zoneGroupById.set(zone.id, zoneGroup);
      });

      const areasByZone = zones.map(zone => {
        const areas = this.areas()
          .filter(area => area.zoneId === zone.id && area.isApplicable !== false);

        return areas.map(area => ({
          ...area,
          zoneGroup: zoneGroupById.get(zone.id) ?? this.defaultZoneGroup
        }));
      });

      const uniqueAreas = new Map<string, SelectableArea>();
      areasByZone.flat().forEach(area => {
        if (!uniqueAreas.has(area.identifier)) {
          uniqueAreas.set(area.identifier, area);
        }
      });

      const orderedAreas = Array.from(uniqueAreas.values())
        .sort((left, right) => {
          const leftOrder = left.sortingOrder ?? Number.MAX_SAFE_INTEGER;
          const rightOrder = right.sortingOrder ?? Number.MAX_SAFE_INTEGER;

          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }

          return left.name.localeCompare(right.name);
        });

      this.roomOptions.set(orderedAreas);

      if (this.hasInteriorRooms()) {
        this.selectedZoneGroup.set(CONSTANTS.ZONES.INTERIOR as ZoneGroup);
      } else if (this.hasExteriorRooms()) {
        this.selectedZoneGroup.set(CONSTANTS.ZONES.EXTERIOR as ZoneGroup);
      } else {
        this.selectedZoneGroup.set(this.defaultZoneGroup);
      }

      const selectedAreaIdentifier = this.selectedAreaIdentifier();
      if (selectedAreaIdentifier) {
        const selectedRoomStillAvailable = orderedAreas.some(area => area.identifier === selectedAreaIdentifier);
        if (!selectedRoomStillAvailable) {
          this.selectedAreaIdentifier.set(null);
        }
      }
    } finally {
      this.loading.set(false);
    }
  }

  onZoneGroupChange(zoneGroup: ZoneGroup): void {
    this.selectedZoneGroup.set(zoneGroup);

    const selectedAreaIdentifier = this.selectedAreaIdentifier();
    if (!selectedAreaIdentifier) {
      return;
    }

    const selectedAreaStillVisible = this.availableRooms().some(area => area.identifier === selectedAreaIdentifier);
    if (!selectedAreaStillVisible) {
      this.selectedAreaIdentifier.set(null);
    }
  }

  onSelectRoom(area: SelectableArea): void {
    this.selectedAreaIdentifier.set(area.identifier);
  }

  onCancel(): void {
    this.selectedAreaIdentifier.set(null);
    this.isOpenChange.emit(false);
  }

  onAssign(): void {
    const selectedArea = this.availableRooms().find(area => area.identifier === this.selectedAreaIdentifier());

    if (!selectedArea) {
      return;
    }

    this.roomAssigned.emit({
      ...selectedArea,
      syncStatus: selectedArea.syncStatus ?? SyncableStatus.Completed,
      updatedAt: selectedArea.updatedAt ?? new Date()
    });

    this.selectedAreaIdentifier.set(null);
    this.isOpenChange.emit(false);
  }

  isRoomSelected(area: SelectableArea): boolean {
    return this.selectedAreaIdentifier() === area.identifier;
  }
}
